import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Star, MessageCircle, Clock, Shield, Search, X, AlertCircle, User, CheckCircle, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import ChatInterface from './chat/ChatInterface';
import AdvisorReviews from './advisor/AdvisorReviews';

interface Advisor {
  id: string;
  display_name: string;
  specialty: string;
  rating: number | null;
  per_message_rate: number | null;
  experience_years: number | null;
  profile_image_url: string | null;
  status: string;
  bio: string | null;
  total_readings: number | null;
  admin_user: boolean | null;
}

interface ActiveChat {
  id: string;
  advisor_id: string;
  client_id: string;
  status: string;
  advisor: {
    display_name: string;
    profile_image_url: string | null;
  };
  updated_at: string;
}

const FUNDING_OPTIONS = [
  { amount: 10, label: '$10' },
  { amount: 25, label: '$25' },
  { amount: 50, label: '$50' }
];

export default function Advisors() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [advisors, setAdvisors] = useState<Advisor[]>([]);
  const [selectedAdvisor, setSelectedAdvisor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    specialty: '',
    priceRange: '',
    availability: '',
    rating: ''
  });
  const [selectedAdvisorForReviews, setSelectedAdvisorForReviews] = useState<string | null>(null);
  const [selectedAdvisorName, setSelectedAdvisorName] = useState<string | null>(null);
  const channelRef = useRef<any>(null);
  const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [expandedBio, setExpandedBio] = useState<string | null>(null);
  const [showChatModal, setShowChatModal] = useState(true);
  const [freeMessagesRemaining, setFreeMessagesRemaining] = useState<number>(0);
  const [freeMessagesUsed, setFreeMessagesUsed] = useState<boolean>(false);
  const [walletBalance, setWalletBalance] = useState<number>(0);

  useEffect(() => {
    fetchAdvisors();
    const statusChannel = setupRealtimeSubscription();
    
    if (location.state?.activeChat) {
      console.log('Found active chat in location state:', location.state.activeChat);
      setSelectedAdvisor(location.state.activeChat.advisor);
      setCurrentConversationId(location.state.activeChat.id);
      
      window.history.replaceState({}, document.title);
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }
      
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
        requestTimeoutRef.current = null;
      }

      if (statusChannel) {
        statusChannel.unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchFreeMessagesRemaining();
      fetchWalletBalance();
    }
  }, [user]);

  const fetchFreeMessagesRemaining = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('message_credits')
        .select('credits_remaining, free_messages_used')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching free messages:', error);
        return;
      }

      setFreeMessagesRemaining(data?.credits_remaining || 0);
      setFreeMessagesUsed(data?.free_messages_used || false);
      console.log('Free messages remaining:', data?.credits_remaining, 'Used:', data?.free_messages_used);
    } catch (err) {
      console.error('Error fetching free messages:', err);
    }
  };

  const fetchWalletBalance = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching wallet balance:', error);
        return;
      }

      setWalletBalance(data?.balance || 0);
      console.log('Wallet balance:', data?.balance);
    } catch (err) {
      console.error('Error fetching wallet balance:', err);
    }
  };

  const setupRealtimeSubscription = () => {
    console.log('Setting up realtime subscription for advisor status changes');
    
    const advisorStatusChannel = supabase.channel('advisor-status-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_type=eq.advisor`
        },
        (payload) => {
          console.log('Advisor profile update received:', payload);
          
          if (payload.new && payload.old && payload.new.status !== payload.old.status) {
            console.log(`Advisor ${payload.new.display_name} status changed from ${payload.old.status} to ${payload.new.status}`);
            
            setAdvisors(currentAdvisors => 
              currentAdvisors.map(advisor => 
                advisor.id === payload.new.id 
                  ? { ...advisor, status: payload.new.status }
                  : advisor
              )
            );
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
        
        if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
          console.log('Subscription error, attempting to reconnect...');
          
          setTimeout(() => {
            setupRealtimeSubscription();
          }, 2000);
        }
      });
      
    return advisorStatusChannel;
  };

  useEffect(() => {
    if (currentConversationId) {
      const channel = supabase
        .channel(`conversation-${currentConversationId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'conversations',
            filter: `id=eq.${currentConversationId}`
          },
          (payload) => {
            console.log('Conversation update:', payload);
            
            if (payload.new.status === 'active') {
              setTimeout(() => {
                const advisor = advisors.find(a => a.id === payload.new.advisor_id);
                if (advisor) {
                  setSelectedAdvisor({
                    id: advisor.id,
                    name: advisor.display_name,
                    image: advisor.profile_image_url || 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&h=250',
                    status: 'busy',
                    per_message_rate: advisor.per_message_rate || 0
                  });
                  setWaitingForResponse(false);
                  setShowTimeoutMessage(false);
                  
                  setAdvisors(currentAdvisors => 
                    currentAdvisors.map(a => 
                      a.id === advisor.id 
                        ? { ...a, status: 'busy' }
                        : a
                    )
                  );
                }
              }, 300);
            } else if (payload.new.status === 'declined') {
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
              }
              
              if (requestTimeoutRef.current) {
                clearTimeout(requestTimeoutRef.current);
                requestTimeoutRef.current = null;
              }
              
              setError('Advisor declined the chat request');
              setWaitingForResponse(false);
              setShowTimeoutMessage(false);
              setCurrentConversationId(null);
            } else if (payload.new.status === 'closed') {
              console.log('Conversation closed, fetching actual advisor status from database');
              
              // Don't trust payload.new for advisor status
              // Instead, wait a moment and fetch the actual status from the database
              setTimeout(async () => {
                try {
                  // Fetch the advisor's current status directly from the database
                  const { data: advisorData, error: advisorError } = await supabase
                    .from('profiles')
                    .select('status')
                    .eq('id', payload.new.advisor_id)
                    .single();
                    
                  if (advisorError) {
                    console.error('Error fetching advisor status:', advisorError);
                    // Fallback to full refresh if direct fetch fails
                    fetchAdvisors();
                    return;
                  }
                  
                  console.log(`Fetched actual advisor status: ${advisorData.status}`);
                  
                  // Update the advisor's status in the UI with the actual status from DB
                  setAdvisors(currentAdvisors => 
                    currentAdvisors.map(a => 
                      a.id === payload.new.advisor_id
                        ? { ...a, status: advisorData.status }
                        : a
                    )
                  );
                } catch (err) {
                  console.error('Error updating advisor status after chat close:', err);
                  // Fallback to full refresh if anything fails
                  fetchAdvisors();
                }
              }, 1500); // Wait 1.5 seconds to ensure database updates have completed
            }
            // Don't clear selectedAdvisor or currentConversationId when status is 'closed'
            // Let the ChatInterface component handle the chat ended state
          }
        )
        .subscribe((status) => {
          console.log(`Realtime subscription status for chat-${currentConversationId}:`, status);
          
          if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
            console.log('Subscription error, attempting to reconnect...');
            
            setTimeout(() => {
              if (currentConversationId) {
                console.log('Attempting to reconnect to realtime subscription...');
                setupRealtimeSubscription();
              }
            }, 2000);
          }
        });

      channelRef.current = channel;
      
      // Set up a 30-second timeout for advisor response
      requestTimeoutRef.current = setTimeout(() => {
        if (waitingForResponse && currentConversationId) {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          
          setShowTimeoutMessage(true);
          setWaitingForResponse(false);
          
          console.log('Request timed out, but keeping conversation in pending state');
        }
      }, 30000); // 30 seconds timeout
      
      return () => {
        channel.unsubscribe();
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        if (requestTimeoutRef.current) {
          clearTimeout(requestTimeoutRef.current);
          requestTimeoutRef.current = null;
        }
      };
    }
  }, [currentConversationId, advisors, waitingForResponse]);

  useEffect(() => {
    const checkForActiveChats = async () => {
      if (!user) return;
      
      try {
        const { data, error: chatError } = await supabase
          .from('conversations')
          .select(`
            id,
            advisor_id,
            client_id,
            status,
            advisor:advisor_id (
              display_name,
              profile_image_url,
              per_message_rate
            )
          `)
          .eq('client_id', user.id)
          .eq('status', 'active')
          .maybeSingle();
          
        if (chatError && chatError.code !== 'PGRST116') {
          console.error('Error checking for active chats:', chatError);
          return;
        }
        
        if (data) {
          console.log('Found active chat:', data);
          setSelectedAdvisor({
            id: data.advisor_id,
            name: data.advisor.display_name,
            image: data.advisor.profile_image_url || 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&h=250',
            status: 'busy',
            per_message_rate: data.advisor.per_message_rate || 0
          });
          setCurrentConversationId(data.id);
        }
      } catch (err) {
        console.error('Error checking for active chats:', err);
      }
    };
    
    checkForActiveChats();
  }, [user]);

  const fetchAdvisors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select(`
          id,
          display_name,
          specialty,
          bio,
          rating,
          experience_years,
          profile_image_url,
          per_message_rate,
          status,
          total_readings,
          admin_user
        `)
        .eq('user_type', 'advisor')
        .eq('approved', true)
        .is('admin_user', false);

      if (fetchError) throw fetchError;
      if (!data) throw new Error('No advisors found');

      const advisorsWithRatings = await Promise.all(
        data.map(async (advisor) => {
          try {
            const { data: ratingsData, error: ratingsError } = await supabase
              .from('advisor_ratings')
              .select('rating')
              .eq('advisor_id', advisor.id);
              
            if (ratingsError) {
              console.error(`Error fetching ratings for advisor ${advisor.id}:`, ratingsError);
              return advisor;
            }
            
            if (ratingsData && ratingsData.length > 0) {
              const sum = ratingsData.reduce((acc, curr) => acc + curr.rating, 0);
              const avgRating = sum / ratingsData.length;
              
              return {
                ...advisor,
                rating: advisor.rating || avgRating
              };
            }
            
            return advisor;
          } catch (err) {
            console.error(`Error processing ratings for advisor ${advisor.id}:`, err);
            return advisor;
          }
        })
      );

      setAdvisors(advisorsWithRatings);
    } catch (err) {
      console.error('Error fetching advisors:', err);
      setError('Failed to load advisors. Please try again later.');
      
      // Set default advisors on error
      setAdvisors([
        {
          id: '1',
          display_name: "Luna Starlight",
          specialty: "Tarot & Love Readings",
          rating: 4.9,
          per_message_rate: 4.99,
          experience_years: 15,
          profile_image_url: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=400",
          status: "online",
          bio: "Specializing in love and relationship readings with tarot guidance",
          total_readings: 250,
          admin_user: false
        },
        {
          id: '2',
          display_name: "Mystic River",
          specialty: "Career & Finance Readings",
          rating: 4.8,
          per_message_rate: 5.99,
          experience_years: 10,
          profile_image_url: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=400",
          status: "online",
          bio: "Expert in career path guidance and financial destiny readings",
          total_readings: 180,
          admin_user: false
        },
        {
          id: '3',
          display_name: "Crystal Seer",
          specialty: "Spiritual Guidance & Readings",
          rating: 4.7,
          per_message_rate: 3.99,
          experience_years: 8,
          profile_image_url: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=400",
          status: "online",
          bio: "Intuitive spiritual advisor specializing in life path guidance",
          total_readings: 120,
          admin_user: false
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const playNotificationSound = async () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const audio = new Audio('https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3');
      audio.loop = true;
      audio.volume = 0.5;
      audioRef.current = audio;

      try {
        await audio.play();
        console.log('Marimba sound played successfully');
      } catch (primaryError) {
        console.warn('Marimba sound failed, trying fallback:', primaryError);

        const fallbackSound = 'https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3';
        console.log('Trying fallback sound:', fallbackSound);
        const fallbackAudio = new Audio(fallbackSound);
        fallbackAudio.loop = true;
        fallbackAudio.volume = 0.5;
        audioRef.current = fallbackAudio;

        try {
          await fallbackAudio.play();
        } catch (fallbackError) {
          console.warn('Fallback sound also failed, trying alternative:', fallbackError);

          const alternativeSounds = [
            '/sounds/bell.mp3',
            '/sounds/chime.mp3',
            '/sounds/ding.mp3',
            '/sounds/soft.mp3'
          ];

          for (const soundPath of alternativeSounds) {
            try {
              const alternativeAudio = new Audio(soundPath);
              alternativeAudio.loop = true;
              alternativeAudio.volume = 0.5;
              audioRef.current = alternativeAudio;
              await alternativeAudio.play();
              break;
            } catch (altError) {
              console.warn(`Alternative sound ${soundPath} failed:`, altError);
              continue;
            }
          }
        }
      }
    } catch (err) {
      console.error('All notification sounds failed:', err);
    }
  };

  const sendPushNotification = async (advisorId: string, clientName: string, type: 'chat_request', retryCount = 0) => {
    try {
      console.log(`Sending push notification to advisor ${advisorId}`);
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-push-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          advisorId,
          clientName,
          type
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send push notification');
      }

      const data = await response.json();
      console.log('Push notification response:', data);
      return data.success;
    } catch (err) {
      console.error(`Push notification attempt ${retryCount + 1} failed:`, err);
      
      if (retryCount < 3) { // Assuming maxRetries is 3
        const delay = 1000 * Math.pow(2, retryCount); // Exponential backoff
        console.log(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return sendPushNotification(advisorId, clientName, type, retryCount + 1);
      }
      
      console.error('Max retry attempts reached for push notification');
      return false;
    }
  };

  const handleStartChat = useCallback(async (advisor: Advisor) => {
    if (!user) {
      navigate('/login', { state: { from: '/advisors' } });
      return;
    }

    if (advisor.status !== 'online') {
      if (advisor.status === 'busy') {
        setError('This advisor is currently busy with another client. Please try again later or choose another advisor.');
      } else {
        setError('This advisor is currently offline');
      }
      return;
    }

    // Check if client has free messages or enough wallet balance
    try {
      // First, check if there's already an active or pending conversation with this advisor
      const { data: existingConversation, error: existingError } = await supabase
        .from('conversations')
        .select('id, status')
        .eq('client_id', user.id)
        .eq('advisor_id', advisor.id)
        .in('status', ['active', 'pending'])
        .maybeSingle();
        
      if (existingError && existingError.code !== 'PGRST116') {
        console.error('Error checking existing conversations:', existingError);
        throw new Error('Failed to check existing conversations');
      }
      
      // If there's an existing conversation, redirect to it instead of creating a new one
      if (existingConversation) {
        console.log('Found existing conversation:', existingConversation);
        
        if (existingConversation.status === 'active') {
          // If it's active, navigate directly to the chat
          navigate(`/chat/${existingConversation.id}`);
          return;
        } else {
          // If it's pending, set it as the current conversation and show waiting modal
          setCurrentConversationId(existingConversation.id);
          setWaitingForResponse(true);
          setShowTimeoutMessage(false);
          playNotificationSound();
          
          // Get client's display name for the notification
          const { data: clientProfile } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('id', user.id)
            .single();
            
          const clientName = clientProfile?.display_name || user?.email || 'Anonymous';
          
          // Send push notification to advisor's mobile device
          await sendPushNotification(advisor.id, clientName, 'chat_request');
          
          return;
        }
      }

      // Fetch free messages info
      const { data: messageCredits, error: creditsError } = await supabase
        .from('message_credits')
        .select('credits_remaining, free_messages_used')
        .eq('user_id', user.id)
        .single();
        
      if (creditsError && creditsError.code !== 'PGRST116') {
        console.error('Error fetching message credits:', creditsError);
        throw new Error('Failed to check message credits');
      }
      
      // Fetch wallet balance
      const { data: wallet, error: walletError } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user.id)
        .single();
        
      if (walletError && walletError.code !== 'PGRST116') {
        console.error('Error fetching wallet:', walletError);
        throw new Error('Failed to check wallet balance');
      }
      
      const hasFreeMessages = messageCredits?.credits_remaining > 0 && !messageCredits?.free_messages_used;
      const hasEnoughBalance = (wallet?.balance || 0) >= (advisor.per_message_rate || 0);
      
      if (!hasFreeMessages && !hasEnoughBalance) {
        setError(`Insufficient funds to start a chat. Please add funds to your wallet. Message price: $${advisor.per_message_rate?.toFixed(2)}`);
        return;
      }
      
      setError(null);
      setWaitingForResponse(true);
      setShowTimeoutMessage(false);

      const { data: newConversation, error: insertError } = await supabase
        .from('conversations')
        .insert({
          client_id: user.id,
          advisor_id: advisor.id,
          status: 'pending'
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating conversation:', insertError);
        if (insertError.message.includes('violates row-level security policy')) {
          throw new Error('Unable to start chat due to permission issues. Please try again or contact support.');
        }
        throw insertError;
      }

      console.log('Created new conversation:', newConversation);
      setCurrentConversationId(newConversation.id);
      playNotificationSound();

      // Get client's display name for the notification
      const { data: clientProfile, error: clientError } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .single();
        
      if (clientError) {
        console.error('Error fetching client profile:', clientError);
        // Continue with a default name if there's an error
      }
      
      const clientName = clientProfile?.display_name || user?.email || 'Anonymous';
      
      // Send push notification to advisor's mobile device
      await sendPushNotification(advisor.id, clientName, 'chat_request');

    } catch (err) {
      console.error('Error starting chat:', err);
      if (err instanceof Error && err.message.includes('duplicate key value violates unique constraint')) {
        setError('Chat request already sent. Please wait for advisor response.');
      } else {
        setError('Failed to start chat. Please try again.');
      }
      setWaitingForResponse(false);
      setShowTimeoutMessage(false);
      setCurrentConversationId(null);
      
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [user, navigate]);

  const filteredAdvisors = advisors.filter(advisor => {
    const matchesSearch = advisor.display_name.toLowerCase().includes(filters.search.toLowerCase()) ||
                         (advisor.specialty || '').toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesSpecialty = !filters.specialty || (advisor.specialty || '').toLowerCase().includes(filters.specialty.toLowerCase());
    
    const matchesPriceRange = !filters.priceRange || (
      (filters.priceRange === 'low' && (advisor.per_message_rate || 0) < 5) ||
      (filters.priceRange === 'medium' && (advisor.per_message_rate || 0) >= 5 && (advisor.per_message_rate || 0) <= 10) ||
      (filters.priceRange === 'high' && (advisor.per_message_rate || 0) > 10)
    );
    
    const matchesAvailability = !filters.availability || advisor.status === filters.availability;
    
    const matchesRating = !filters.rating || 
      (advisor.rating !== null && advisor.rating >= parseInt(filters.rating));

    return matchesSearch && matchesSpecialty && matchesPriceRange && matchesAvailability && matchesRating;
  });

  const handleChatClose = useCallback(() => {
    console.log('Chat closed - clearing selected advisor');
    setSelectedAdvisor(null);
    setWaitingForResponse(false);
    setShowTimeoutMessage(false);
    setCurrentConversationId(null);
    setShowChatModal(false);
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = null;
    }
    
    fetchAdvisors();
  }, []);

  const handleCancelRequest = () => {
    setWaitingForResponse(false);
    setShowTimeoutMessage(false);
    setCurrentConversationId(null);
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = null;
    }
    
    console.log('Request canceled by client, but keeping conversation in pending state');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Our Gifted Advisors</h1>
          <p className="text-xl text-purple-100">
            Connect with experienced psychic advisors for personalized readings
          </p>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-50 rounded-lg flex items-center justify-between text-red-700">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              <p>{error}</p>
            </div>
            <button 
              onClick={() => setError(null)} 
              className="text-red-700 hover:text-red-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="Search advisors..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/60"
            />
            <select 
              value={filters.specialty}
              onChange={(e) => setFilters({ ...filters, specialty: e.target.value })}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/20 text-white"
            >
              <option value="">All Specialties</option>
              <option value="tarot">Tarot Reading</option>
              <option value="love">Love & Relationships</option>
              <option value="career">Career & Finance</option>
            </select>
            <select 
              value={filters.priceRange}
              onChange={(e) => setFilters({ ...filters, priceRange: e.target.value })}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/20 text-white"
            >
              <option value="">Price Range</option>
              <option value="low">Under $5/msg</option>
              <option value="medium">$5-$10/msg</option>
              <option value="high">Over $10/msg</option>
            </select>
            <select 
              value={filters.availability}
              onChange={(e) => setFilters({ ...filters, availability: e.target.value })}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/20 text-white"
            >
              <option value="">Availability</option>
              <option value="online">Online Now</option>
              <option value="busy">Busy</option>
              <option value="offline">Offline</option>
            </select>
            <select 
              value={filters.rating}
              onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/20 text-white"
            >
              <option value="">Rating</option>
              <option value="4">4+ Stars</option>
              <option value="3">3+ Stars</option>
              <option value="2">2+ Stars</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredAdvisors.map((advisor) => (
            <div key={advisor.id} className="bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="relative">
                <img 
                  key={advisor.profile_image_url || 'default-image'}
                  src={advisor.profile_image_url || 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&h=250'} 
                  alt={advisor.display_name}
                  className="w-full h-64 object-cover"
                />
                <div className={`absolute top-4 right-4 px-3 py-1 rounded-full ${
                  advisor.status === 'online' ? 'bg-green-500' : 
                  advisor.status === 'busy' ? 'bg-orange-500' : 
                  'bg-gray-500'
                } text-white text-sm`}>
                  {advisor.status === 'online' ? 'Online' : 
                   advisor.status === 'busy' ? 'Busy' : 
                   'Offline'}
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{advisor.display_name}</h3>
                <p className="text-purple-600 mb-2">{advisor.specialty}</p>
                
                <div className="flex items-center mb-2">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`w-4 h-4 ${
                          star <= Math.round(advisor.rating || 0)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="ml-1">{advisor.rating ? advisor.rating.toFixed(1) : '0.0'}</span>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAdvisorForReviews(advisor.id);
                      setSelectedAdvisorName(advisor.display_name);
                    }}
                    className="ml-2 text-xs text-purple-600 hover:text-purple-800"
                  >
                    See Reviews
                  </button>
                  <Clock className="ml-4 w-5 h-5 text-gray-500" />
                  <span className="ml-1">{advisor.experience_years || 0} years</span>
                </div>

                <div className="flex items-center mb-2 text-sm text-gray-600">
                  <span className="mr-1">Total Readings:</span>
                  <span className="font-medium">{advisor.total_readings || 0}</span>
                </div>

                {expandedBio === advisor.id ? (
                  <div className="mb-4">
                    <p className="text-gray-600">{advisor.bio || 'Experienced psychic advisor ready to provide guidance and insights.'}</p>
                    <button 
                      onClick={() => setExpandedBio(null)}
                      className="text-purple-600 text-sm mt-1 hover:text-purple-800"
                    >
                      Show less
                    </button>
                  </div>
                ) : (
                  <div className="mb-4">
                    <p className="text-gray-600 line-clamp-2">
                      {advisor.bio || 'Experienced psychic advisor ready to provide guidance and insights.'}
                    </p>
                    {advisor.bio && advisor.bio.length > 100 && (
                      <button 
                        onClick={() => setExpandedBio(advisor.id)}
                        className="text-purple-600 text-sm mt-1 hover:text-purple-800"
                      >
                        Read more
                      </button>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Price per message:</span>
                    <span className="text-xl font-bold">${(advisor.per_message_rate || 0).toFixed(2)}</span>
                  </div>
                  
                  <button 
                    onClick={() => handleStartChat(advisor)}
                    disabled={advisor.status !== 'online' || (!user || (!freeMessagesRemaining && !freeMessagesUsed && walletBalance < (advisor.per_message_rate || 0)))}
                    className={`${
                      advisor.status === 'online'
                        ? user 
                          ? freeMessagesRemaining > 0 && !freeMessagesUsed
                            ? 'bg-purple-600 hover:bg-purple-700'
                            : walletBalance >= (advisor.per_message_rate || 0)
                              ? 'bg-purple-600 hover:bg-purple-700'
                              : 'bg-gray-400 cursor-not-allowed'
                          : 'bg-purple-600 hover:bg-purple-700'
                        : advisor.status === 'busy'
                          ? 'bg-orange-400 cursor-not-allowed'
                          : 'bg-gray-400 cursor-not-allowed'
                    } text-white px-4 py-2 rounded-full transition-colors flex items-center justify-center`}
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {advisor.status === 'online' 
                      ? (user 
                          ? (freeMessagesRemaining > 0 && !freeMessagesUsed 
                              ? `Start Chat (${freeMessagesRemaining} free)` 
                              : walletBalance >= (advisor.per_message_rate || 0)
                                ? 'Start Chat'
                                : 'Add Funds to Chat')
                          : 'Login to Chat')
                      : advisor.status === 'busy'
                        ? 'Advisor Busy'
                        : 'Offline'}
                  </button>
                  
                  {user && advisor.status === 'online' && freeMessagesRemaining <= 0 && walletBalance < (advisor.per_message_rate || 0) && (
                    <button
                      onClick={() => navigate('/client/dashboard')}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full transition-colors flex items-center justify-center mt-2"
                    >
                      <DollarSign className="w-5 h-5 mr-2" />
                      Add Funds
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {waitingForResponse && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
              <h2 className="text-2xl font-bold mb-4">Connecting to Advisor</h2>
              <p className="text-gray-600 mb-4">
                Your chat request has been sent. Please wait while the advisor responds.
              </p>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <button
                onClick={handleCancelRequest}
                className="mt-6 text-gray-600 hover:text-gray-800"
              >
                Cancel Request
              </button>
            </div>
          </div>
        )}

        {showTimeoutMessage && !waitingForResponse && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
              <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">No Response</h2>
              <p className="text-gray-600 mb-6">
                This advisor didn't answer. Please try another advisor or check back in a few minutes.
              </p>
              <button
                onClick={() => {
                  setShowTimeoutMessage(false);
                  setCurrentConversationId(null);
                  if (requestTimeoutRef.current) {
                    clearTimeout(requestTimeoutRef.current);
                    requestTimeoutRef.current = null;
                  }
                }}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Browse Advisors
              </button>
            </div>
          </div>
        )}

        {selectedAdvisorForReviews && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-xl font-bold">{selectedAdvisorName}'s Reviews</h2>
                <button
                  onClick={() => setSelectedAdvisorForReviews(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                <AdvisorReviews 
                  advisorId={selectedAdvisorForReviews} 
                  advisorName={selectedAdvisorName || undefined}
                />
              </div>
            </div>
          </div>
        )}

        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 ${
            selectedAdvisor && user && showChatModal ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        >
          {selectedAdvisor && user && (
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full h-[80vh] flex flex-col">
              <div className="p-4 border-b flex justify-between items-center">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{selectedAdvisor.name}</h3>
                    <div className="flex items-center text-sm text-gray-500 space-x-2">
                      <span>${selectedAdvisor.per_message_rate}/msg</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleChatClose}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <ChatInterface
                  advisor={selectedAdvisor}
                  conversationId={currentConversationId || undefined}
                  onClose={handleChatClose}
                  isClient={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}